import { Fragment, useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import styles from "./styles.module.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarDays,
  faListCheck,
  faXmark,
  faClock,
  faThumbtack,
  faPlus,
  faArrowRight,
  faArrowLeft,
  faPen,
  faChevronUp,
  faChevronDown,
  faChevronRight,
  faChevronLeft,
  faMagnifyingGlass,
  faPhone,
  faStickyNote,
  faImage,
  faRotateLeft,
  faCheck,
  faEnvelope,
  faBell,
} from "@fortawesome/free-solid-svg-icons";
import AddNote from "components/AddNote";
import { INote } from "components/NotesDesk";
import deskService, { ITodoDto, IEventDto, IContactDto } from "api/deskService";
import casesService from "api/casesService";
import DeskPackages from "components/DeskPackages";
import { IPackage } from "dto/IPackage";
import Package from "components/Package";
import WelcomeTheme from "./WelcomeTheme";
import {
  getDueOccasionalTheme,
  getOccasionalById,
  recordImpression,
  dismissOccasional,
  resetOccasionalDemo,
  OCCASIONAL_THEMES,
} from "./occasionalThemes";
import { downscaleImageToDataUrl } from "utils/downscaleImage";

interface Theme {
  key: string;
  name: string;
  /** Gradient CSS (motyw bez zdjęcia). */
  bg?: string;
  /** URL/obraz zdjęcia tła. */
  image?: string;
  /** Motyw przezroczysty — dawna maska przesłaniająca (widać biurko pod spodem). */
  transparent?: boolean;
}

// Zdjęcia z publicznego, darmowego źródła Lorem Picsum (deterministyczne wg seeda).
// Docelowo operator dostarczy realne, tematyczne fotografie pod tytuły grup.
const PICSUM_SEED = (seed: string) => `https://picsum.photos/seed/${seed}/1600/1000`;

// Motyw przezroczysty (dawna maska przesłaniająca) — baza/domyślny wybór.
const transparentTheme: Theme = {
  key: "transparent",
  name: "Przezroczysty (maska)",
  transparent: true,
};

interface ThemeGroup {
  key: string;
  title: string;
  themes: Theme[];
}

const photo = (groupKey: string, idx: number, name: string): Theme => ({
  key: `${groupKey}-${idx}`,
  name,
  image: PICSUM_SEED(`${groupKey}-${idx}`),
});

// Grupy kategorii — po 7 fotografii tematycznie nawiązujących do tytułu grupy.
const CATEGORY_GROUPS: ThemeGroup[] = [
  { key: "seasons", title: "Pory roku", themes: ["Wiosna", "Lato", "Jesień", "Zima", "Przedwiośnie", "Babie lato", "Zimowy poranek"].map((n, i) => photo("seasons", i, n)) },
  { key: "landscape", title: "Pejzaż", themes: ["Góry", "Morze", "Las", "Pustynia", "Łąka", "Dolina", "Wodospad"].map((n, i) => photo("landscape", i, n)) },
  { key: "continent", title: "Kontynent", themes: ["Europa", "Azja", "Afryka", "Ameryka Północna", "Ameryka Południowa", "Australia", "Antarktyda"].map((n, i) => photo("continent", i, n)) },
  { key: "country", title: "Kraj", themes: ["Polska", "Włochy", "Norwegia", "Japonia", "Maroko", "Islandia", "Nowa Zelandia"].map((n, i) => photo("country", i, n)) },
  { key: "city", title: "Miasto", themes: ["Kraków", "Paryż", "Tokio", "Nowy Jork", "Wenecja", "Praga", "Lizbona"].map((n, i) => photo("city", i, n)) },
  { key: "fauna", title: "Fauna", themes: ["Wilk", "Orzeł", "Jeleń", "Lis", "Żuraw", "Sarna", "Niedźwiedź"].map((n, i) => photo("fauna", i, n)) },
  { key: "flora", title: "Flora", themes: ["Maki", "Lawenda", "Sosna", "Dąb", "Słoneczniki", "Paproć", "Wrzos"].map((n, i) => photo("flora", i, n)) },
];

const ALL_PHOTO_THEMES: Theme[] = CATEGORY_GROUPS.flatMap((g) => g.themes);

// Motywy podlegające codziennej rotacji („Systemowy codziennie inny").
const ROTATABLE_THEMES = ALL_PHOTO_THEMES;

const THEME_KEY_LS = "desk_planner_theme";
const LAST_THEME_LS = "desk_planner_last_theme"; // ostatni motyw przed wyłączeniem (do przywrócenia)
const CUSTOM_THEMES_LS = "desk_planner_custom_themes";
const WEEKDAY_THEMES_LS = "desk_planner_weekday_themes";
const TAB_ORDER_LS = "desk_planner_tab_order";

type PlannerTab = "cal" | "todo" | "mail";
const DEFAULT_TAB_ORDER: PlannerTab[] = ["cal", "todo", "mail"];
const TAB_META: Record<PlannerTab, { label: string; icon: typeof faCalendarDays }> = {
  cal: { label: "Terminarz", icon: faCalendarDays },
  todo: { label: "Lista ToDo", icon: faListCheck },
  mail: { label: "Nowa poczta / Przypominajki", icon: faEnvelope },
};

// Dni tygodnia: indeks = Date.getDay() (0 = niedziela). Prezentacja od poniedziałku.
const WEEKDAY_LABELS = ["Niedziela", "Poniedziałek", "Wtorek", "Środa", "Czwartek", "Piątek", "Sobota"];
const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

// Motyw dnia tygodnia (obraz wgrany przez użytkownika dla danego dnia).
interface WeekdayTheme {
  image: string;
  name: string;
}

// Klucze wszystkich grup zasobnika (do domyślnego zwijania).
const THEME_GROUP_KEYS = [
  "custom",
  "weekday",
  ...CATEGORY_GROUPS.map((g) => g.key),
  "occasional",
];

interface CustomTheme {
  id: string;
  name: string;
  image: string;
}

// Miniatura motywu w menu: przezroczysty = wzór „kratki", zdjęcie = obraz cover,
// gradient = tło CSS.
const swatchStyle = (t: Theme): React.CSSProperties =>
  t.transparent
    ? {
        backgroundColor: "#fff",
        backgroundImage:
          "linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%), linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%)",
        backgroundSize: "10px 10px",
        backgroundPosition: "0 0, 5px 5px",
      }
    : t.image
      ? { backgroundImage: `url(${t.image})`, backgroundSize: "cover" }
      : { background: t.bg };

// Codziennie inny motyw systemowy (rotacja po dniu roku) — jak ekran startowy.
const dailySystemTheme = (): Theme => {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 0);
  const day = Math.floor((now.getTime() - start.getTime()) / 86400000);
  return ROTATABLE_THEMES[day % ROTATABLE_THEMES.length];
};

interface Props {
  open: boolean;
  onClose: () => void;
}

interface Evt {
  id: number;
  date: string; // YYYY-MM-DD
  time: string; // HH:MM
  dur: number; // minuty
  title: string;
  sub: string;
  accent: string;
  type?: string; // nazwa typu wydarzenia
  deleted?: boolean; // soft-delete: w sekcji „Usunięte" (kosz Terminarza)
  caseId?: string; // powiązana teczka (sprawa)
  caseName?: string; // tytuł powiązanej teczki
}

// Pozycja wyniku wyszukiwania teczki (z /cases/simple → Result.Items).
type CaseHit = { Id: string; Title: string; Signature?: string };

// Typ wydarzenia (z własnym kolorem akcentu). Lista jest uzupełnialna z karty.
interface EvtType {
  id: number;
  name: string;
  accent: string;
}
const EVT_TYPES_INIT: EvtType[] = [
  { id: 1, name: "Rozprawa", accent: "#1565c0" },
  { id: 2, name: "Spotkanie", accent: "#5d49c7" },
  { id: 3, name: "Termin / deadline", accent: "#c2255c" },
  { id: 4, name: "Telekonferencja", accent: "#2e7d32" },
  { id: 5, name: "Podpisanie dokumentów", accent: "#e6a100" },
];
// Paleta kolorów dla nowo dodawanych typów wydarzeń.
const TYPE_PALETTE = ["#5d49c7", "#1565c0", "#c2255c", "#2e7d32", "#e6a100", "#0097a7", "#6d4c41"];
// Najczęstsze długości wydarzenia / poszukiwanego wolnego slotu (minuty).
const DUR_OPTIONS = [15, 30, 45, 60, 90, 120];

interface EvtForm {
  title: string;
  sub: string;
  typeId: number | null;
  typeName: string;
  caseId: string; // powiązana teczka (puste = brak)
  caseName: string; // tytuł/wpisywany tekst teczki
  dur: number; // czas trwania = wielkość poszukiwanego wolnego slotu
  date: string;
  time: string;
}
const EMPTY_EVT: EvtForm = { title: "", sub: "", typeId: null, typeName: "", caseId: "", caseName: "", dur: 60, date: "", time: "" };

const pad = (n: number) => String(n).padStart(2, "0");
const isoOf = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const toMin = (t: string) => {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
};
const toHHMM = (min: number) => `${pad(Math.floor(min / 60))}:${pad(min % 60)}`;

const WORK_START = 8 * 60;
const WORK_END = 18 * 60;
const SLOT_STEP = 30;

// Kubełek prezentacji zadania względem dnia bieżącego (wyliczany z absolutnej daty).
type Day = "yesterday" | "today" | "tomorrow";
interface Todo {
  id: number;
  text: string;
  done: boolean;
  pin: boolean;
  date: string; // YYYY-MM-DD — absolutna data zadania (trwale zapisywana)
  children: Todo[];
}

// ── Mapowanie drzewa Todo ↔ płaska lista DTO (trwały zapis w BE) ────
// Serwer zwraca pozycje w kolejności Position; rodzic→dziecko po ParentLocalId.
const buildTodoTree = (dtos: ITodoDto[]): Todo[] => {
  const nodes = new Map<string, Todo>();
  const roots: Todo[] = [];
  for (const d of dtos) {
    nodes.set(d.LocalId, {
      id: Number(d.LocalId) || 0,
      text: d.Text ?? "",
      done: !!d.Done,
      pin: !!d.Pin,
      date: d.Date || isoOf(new Date()),
      children: [],
    });
  }
  for (const d of dtos) {
    const node = nodes.get(d.LocalId);
    if (!node) continue;
    const parent = d.ParentLocalId ? nodes.get(d.ParentLocalId) : null;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }
  return roots;
};

// Spłaszczenie drzewa do listy DTO (pre-order; Position = kolejność wystąpienia).
const flattenTodos = (
  list: Todo[],
  parentLocalId: string | null,
  acc: ITodoDto[],
): ITodoDto[] => {
  for (const t of list) {
    acc.push({
      LocalId: String(t.id),
      ParentLocalId: parentLocalId,
      Text: t.text,
      Done: t.done,
      Pin: t.pin,
      Date: t.date,
      Position: acc.length,
    });
    flattenTodos(t.children, String(t.id), acc);
  }
  return acc;
};

// ── „Skontaktuj się": kontakty do wykonania + baza Podmioty ───────
// Podmiot = wpis z bazy „Podmioty" (mock). Realne źródło podłączy BE — patrz
// docs/backend-contacts-api.md. Służy do podpowiadania danych w karcie kontaktu.
interface Podmiot {
  id: number;
  name: string;
  phone: string;
  email: string;
}
// ContactTask = zaplanowany kontakt (z opcjonalnym terminem wykonania).
interface ContactTask {
  id: number;
  name: string;
  purpose: string; // cel kontaktu
  phone: string;
  email: string;
  date: string; // YYYY-MM-DD (kiedy wykonać kontakt)
  time: string; // HH:MM
  done: boolean; // oznaczony jako wykonany → trafia do sekcji Wykonane
  // Notatka powiązana z kontaktem (utworzona formatką AddNote, szyfrowana po stronie BE).
  note?: { id: string; title: string; content: string };
}
interface ContactForm {
  name: string;
  purpose: string;
  phone: string;
  email: string;
  date: string;
  time: string;
}
const EMPTY_CONTACT: ContactForm = { name: "", purpose: "", phone: "", email: "", date: "", time: "" };

// Wcześniej używane cele kontaktu (mock) — do podpowiedzi w polu „Cel kontaktu".
const PURPOSES_INIT: string[] = [
  "Uzgodnienie terminu rozprawy",
  "Przekazanie dokumentów do podpisu",
  "Potwierdzenie spotkania",
  "Ustalenie warunków umowy",
];

// Przykładowa baza Podmiotów (mock) — do podpowiedzi przy wpisywaniu kontaktu.
const PODMIOTY_INIT: Podmiot[] = [
  { id: 101, name: "Jan Kowalski", phone: "+48 601 234 567", email: "j.kowalski@example.pl" },
  { id: 102, name: "Anna Nowak", phone: "+48 602 345 678", email: "a.nowak@example.pl" },
  { id: 103, name: "Kancelaria Zając", phone: "+48 22 123 45 67", email: "biuro@zajac.pl" },
  { id: 104, name: "Urząd Skarbowy Kraków", phone: "+48 12 000 11 22", email: "kontakt@us-krakow.gov.pl" },
  { id: 105, name: "Mecenas Piotr Wójcik", phone: "+48 603 456 789", email: "p.wojcik@example.pl" },
];

// ── operacje na drzewie ───────────────────────────────────────────
const mapTree = (list: Todo[], id: number, fn: (t: Todo) => Todo): Todo[] =>
  list.map((t) => (t.id === id ? fn(t) : { ...t, children: mapTree(t.children, id, fn) }));
const removeInTree = (list: Todo[], id: number): Todo[] =>
  list.filter((t) => t.id !== id).map((t) => ({ ...t, children: removeInTree(t.children, id) }));
const flatten = (list: Todo[]): Todo[] =>
  list.flatMap((t) => [t, ...flatten(t.children)]);
const swapAdjacent = (arr: Todo[], id: number, dir: number): Todo[] => {
  const i = arr.findIndex((x) => x.id === id);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= arr.length) return arr;
  const n = [...arr];
  [n[i], n[j]] = [n[j], n[i]];
  return n;
};

// ── Mapowanie Terminarza i kontaktów ↔ DTO (trwały zapis w BE) ─────
const eventFromDto = (d: IEventDto): Evt => ({
  id: Number(d.LocalId) || 0,
  date: d.Date || isoOf(new Date()),
  time: d.Time || "",
  dur: d.Dur,
  title: d.Title,
  sub: d.Sub || "",
  accent: d.Accent || "#5d49c7",
  type: d.Type || undefined,
  deleted: d.Deleted,
  caseId: d.CaseId || undefined,
  caseName: d.CaseTitle || undefined,
});

const eventToDto = (e: Evt, i: number): IEventDto => ({
  LocalId: String(e.id),
  Date: e.date,
  Time: e.time,
  Dur: e.dur,
  Title: e.title,
  Sub: e.sub,
  Accent: e.accent,
  Type: e.type,
  Deleted: e.deleted ?? false,
  CaseId: e.caseId ?? null,
  CaseTitle: e.caseName ?? null,
  Position: i,
});

const contactFromDto = (d: IContactDto): ContactTask => ({
  id: Number(d.LocalId) || 0,
  name: d.Name,
  purpose: d.Purpose || "",
  phone: d.Phone || "",
  email: d.Email || "",
  date: d.Date || "",
  time: d.Time || "",
  done: d.Done,
  note: d.NoteId
    ? { id: d.NoteId, title: d.NoteTitle || "", content: d.NoteContent || "" }
    : undefined,
});

const contactToDto = (c: ContactTask, i: number): IContactDto => ({
  LocalId: String(c.id),
  Name: c.name,
  Purpose: c.purpose,
  Phone: c.phone,
  Email: c.email,
  Date: c.date || null,
  Time: c.time,
  Done: c.done,
  NoteId: c.note?.id ?? null,
  NoteTitle: c.note?.title ?? null,
  NoteContent: c.note?.content ?? null,
  Position: i,
});

const DeskPlanner = ({ open, onClose }: Props) => {
  const [tab, setTab] = useState<PlannerTab>("cal");
  const [tabOrder, setTabOrder] = useState<PlannerTab[]>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(TAB_ORDER_LS) || "null");
      if (Array.isArray(saved) && saved.length === 3) return saved as PlannerTab[];
    } catch { /* ignore parse errors */ }
    return DEFAULT_TAB_ORDER;
  });
  const [selectedPackage, setSelectedPackage] = useState<IPackage | null>(null);
  const [showAddReminder, setShowAddReminder] = useState(false);
  const [todos, setTodos] = useState<Todo[]>([]);

  // ── Trwały zapis listy ToDo w BE (per użytkownik + aktywny profil) ──
  const { data: todosResp, isSuccess: todosLoaded } = deskService.useGetTodos();
  const syncTodosMutation = deskService.useSyncTodos();
  const todosHydratedRef = useRef(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState("");
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(
    new Set(["yesterday", "tomorrow", "done", "contact", "evtDeleted"]),
  );
  const [collapsedKids, setCollapsedKids] = useState<Set<number>>(new Set());
  const nextId = useRef(1000);

  // ── Trwały zapis Terminarza i kontaktów Biurka (per użytkownik + profil) ──
  const { data: eventsResp, isSuccess: eventsLoaded } = deskService.useGetEvents();
  const syncEventsMutation = deskService.useSyncEvents();
  const eventsHydratedRef = useRef(false);
  const { data: contactsResp, isSuccess: contactsLoaded } = deskService.useGetContacts();
  const syncContactsMutation = deskService.useSyncContacts();
  const contactsHydratedRef = useRef(false);

  // Podbicie licznika id ponad maksimum wczytanych LocalId (bez kolizji nowych pozycji).
  const bumpNextId = (localIds: string[]) => {
    const maxId = localIds.reduce((m, id) => Math.max(m, Number(id) || 0), 0);
    nextId.current = Math.max(nextId.current, maxId + 1);
  };

  // Wczytanie listy z BE przy pierwszym udanym pobraniu (raz — potem stan lokalny
  // jest źródłem prawdy). Przy błędzie GET nie hydratujemy → zapis się nie uruchomi,
  // co chroni przed nadpisaniem danych pustą listą.
  useEffect(() => {
    if (todosHydratedRef.current || !todosLoaded) return;
    const dtos = todosResp?.data?.Todos ?? [];
    setTodos(buildTodoTree(dtos));
    const maxId = dtos.reduce((m, d) => Math.max(m, Number(d.LocalId) || 0), 0);
    nextId.current = Math.max(nextId.current, maxId + 1);
    todosHydratedRef.current = true;
  }, [todosLoaded, todosResp]);

  // Po każdej zmianie listy — debounce'owany zapis całości do BE (zastąp wszystko).
  // Uruchamiany dopiero po hydracji, by nie nadpisać bazy stanem początkowym.
  useEffect(() => {
    if (!todosHydratedRef.current) return;
    const handle = setTimeout(() => {
      syncTodosMutation.mutate({ Todos: flattenTodos(todos, null, []) });
    }, 800);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todos]);

  // „Skontaktuj się": lista zaplanowanych kontaktów + baza Podmioty + karta.
  const [contacts, setContacts] = useState<ContactTask[]>([]);
  const [podmioty, setPodmioty] = useState<Podmiot[]>(PODMIOTY_INIT);
  const [purposes, setPurposes] = useState<string[]>(PURPOSES_INIT);
  const [contactForm, setContactForm] = useState<ContactForm | null>(null);
  const [purposeMenuOpen, setPurposeMenuOpen] = useState(false);
  // Kontakt, dla którego otwarta jest formatka notatki (AddNote).
  const [noteContactId, setNoteContactId] = useState<number | null>(null);

  // Hydracja kontaktów z BE (raz) + zapis całości po zmianie (debounce).
  useEffect(() => {
    if (contactsHydratedRef.current || !contactsLoaded) return;
    const dtos = contactsResp?.data?.Contacts ?? [];
    setContacts(dtos.map(contactFromDto));
    bumpNextId(dtos.map((d) => d.LocalId));
    contactsHydratedRef.current = true;
  }, [contactsLoaded, contactsResp]);

  useEffect(() => {
    if (!contactsHydratedRef.current) return;
    const handle = setTimeout(() => {
      syncContactsMutation.mutate({ Contacts: contacts.map(contactToDto) });
    }, 800);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts]);

  // ── Motyw tła slajdu (zamiast zwykłej maski przesłaniającej) ──────────────
  // Rozróżniamy PODGLĄD od motywu DOMYŚLNEGO:
  //  • themeKey        — aktualnie prezentowany podgląd (sesyjny, nieutrwalany),
  //  • defaultThemeKey — motyw domyślny zapisany w localStorage (THEME_KEY_LS),
  //    ładowany przy starcie. Klik w zasobniku = podgląd; utrwalenie wymaga
  //    osobnej akcji „Ustaw jako domyślny" (acceptAsDefault).
  // Wartości: "transparent", "auto" (rotacja), "weekday"/"weekday-N", id motywu
  // własnego (custom-…) lub klucz zdjęcia kategorii.
  const [defaultThemeKey, setDefaultThemeKey] = useState<string>(
    () => localStorage.getItem(THEME_KEY_LS) || "transparent",
  );
  const [themeKey, setThemeKey] = useState<string>(
    () => localStorage.getItem(THEME_KEY_LS) || "transparent",
  );
  // Ostatni aktywny motyw przed wyłączeniem (×) — do przywrócenia.
  const [lastTheme, setLastTheme] = useState<string>(
    () => localStorage.getItem(LAST_THEME_LS) || "",
  );
  const [customThemes, setCustomThemes] = useState<CustomTheme[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(CUSTOM_THEMES_LS) || "[]");
    } catch {
      return [];
    }
  });
  const [themeMenuOpen, setThemeMenuOpen] = useState(false);
  // Zwijanie grup w menu motywów — domyślnie WSZYSTKIE zwinięte.
  const [collapsedThemeGroups, setCollapsedThemeGroups] = useState<Set<string>>(
    () => new Set(THEME_GROUP_KEYS),
  );
  const themeFileRef = useRef<HTMLInputElement>(null);

  // Motywy „7 dni tygodnia" — obraz per dzień (indeks = Date.getDay()).
  const [weekdayThemes, setWeekdayThemes] = useState<(WeekdayTheme | null)[]>(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(WEEKDAY_THEMES_LS) || "null");
      return Array.isArray(raw) && raw.length === 7 ? raw : Array(7).fill(null);
    } catch {
      return Array(7).fill(null);
    }
  });
  const weekdayFileRef = useRef<HTMLInputElement>(null);
  const weekdayTargetRef = useRef<number | null>(null);

  // Otwarcie zasobnika zawsze prezentuje grupy w stanie zwiniętym.
  const openThemeMenu = () => {
    setThemeMenuOpen((o) => {
      if (!o) setCollapsedThemeGroups(new Set(THEME_GROUP_KEYS));
      return !o;
    });
  };

  // ── Motyw okazjonalny (kampania operatora) — nadrzędny nad wyborem usera ──
  // Zamknięte w tej sesji (miękko ×) lub trwale odrzucone trzymamy w jednym
  // zbiorze; trwałe odrzucenie zapisuje też occasionalThemes (BE: …/dismiss).
  const [occasionalClosed, setOccasionalClosed] = useState<Set<string>>(new Set());
  const impressedRef = useRef<Set<string>>(new Set());
  // Auto: kampania należna wg harmonogramu (z pominięciem zamkniętych w sesji).
  const dueOccasional = getDueOccasionalTheme();
  const occasional =
    dueOccasional && !occasionalClosed.has(dueOccasional.id) ? dueOccasional : null;
  // Ręczny podgląd z menu (grupa „Motywy okazjonalne") — ma pierwszeństwo nad auto.
  const manualOccasional = getOccasionalById(themeKey);
  const activeOccasional = manualOccasional ?? occasional;

  // Zliczenie wyświetlenia raz na sesję, gdy kampania faktycznie się pokazuje.
  useEffect(() => {
    if (open && occasional && !impressedRef.current.has(occasional.id)) {
      impressedRef.current.add(occasional.id);
      recordImpression(occasional.id);
    }
  }, [open, occasional]);

  const closeOccasional = () => {
    // Auto: zamknij na czas sesji (wróci wg kadencji). Ręczny podgląd: odznacz → auto.
    if (manualOccasional) chooseTheme("transparent");
    else if (occasional) setOccasionalClosed((s) => new Set(s).add(occasional.id));
  };
  const dismissOccasionalPerm = () => {
    if (!occasional) return;
    dismissOccasional(occasional.id);
    setOccasionalClosed((s) => new Set(s).add(occasional.id));
  };
  // DEMO: wyczyść stan atrapy i wróć do początku pętli (powitalny → webinar).
  const handleResetDemo = () => {
    resetOccasionalDemo();
    impressedRef.current = new Set();
    setOccasionalClosed(new Set());
  };

  // Lewa krawędź motywu zsynchronizowana z prawą krawędzią paska menu.
  // Mierzymy lewą krawędź obszaru treści (rodzic overlaya = kontener Biurka),
  // który zaczyna się dokładnie za menu, i reagujemy na zmiany jego szerokości.
  const overlayRef = useRef<HTMLDivElement>(null);
  const [leftOffset, setLeftOffset] = useState<number | null>(null);

  useEffect(() => {
    const parent = overlayRef.current?.parentElement;
    if (!parent) return;
    let raf = 0;
    let last = -1;
    // Pomiar odłożony do następnej klatki + zapis tylko przy realnej zmianie —
    // zapobiega pętli sprzężenia ResizeObserver (chwilowe zacięcia przy dużych
    // zmianach layoutu, np. zniknięciu całej sekcji listy ToDo po przeniesieniu).
    const measure = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const left = Math.round(parent.getBoundingClientRect().left);
        if (left !== last) {
          last = left;
          setLeftOffset(left);
        }
      });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(parent);
    window.addEventListener("resize", measure);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [open]);

  // Motyw bieżącego dnia tygodnia (gdy aktywny tryb „7 dni tygodnia").
  const weekdayActiveTheme = (): Theme => {
    const wd = weekdayThemes[new Date().getDay()];
    return wd ? { key: "weekday", name: wd.name, image: wd.image } : transparentTheme;
  };

  // Aktywny motyw: transparent (baza), auto → rotacja dzienna, weekday → motyw
  // bieżącego dnia, custom → kolekcja, w innym razie zdjęcie kategorii;
  // fallback do przezroczystego (domyślny).
  const resolveTheme = (): Theme => {
    if (themeKey === "transparent") return transparentTheme;
    if (themeKey === "auto") return dailySystemTheme();
    if (themeKey === "weekday") return weekdayActiveTheme();
    // Pojedynczy motyw dnia wybrany z grupy (bez trybu codziennej zmiany).
    if (themeKey.startsWith("weekday-")) {
      const wd = weekdayThemes[Number(themeKey.slice(8))];
      return wd ? { key: themeKey, name: wd.name, image: wd.image } : transparentTheme;
    }
    const custom = customThemes.find((c) => c.id === themeKey);
    if (custom) return { key: custom.id, name: custom.name, image: custom.image };
    return ALL_PHOTO_THEMES.find((t) => t.key === themeKey) ?? transparentTheme;
  };
  const activeTheme = resolveTheme();

  // Czytelna nazwa motywu dla danego klucza (pasek akceptacji domyślnego).
  const themeNameOf = (key: string): string => {
    if (key === "transparent") return transparentTheme.name;
    if (key === "auto") return "Codzienna rotacja";
    if (key === "weekday") return "7 dni tygodnia";
    if (key.startsWith("weekday-")) {
      const wd = weekdayThemes[Number(key.slice(8))];
      return wd ? `${WEEKDAY_LABELS[Number(key.slice(8))]}: ${wd.name}` : "—";
    }
    const occ = getOccasionalById(key);
    if (occ)
      return occ.kind === "welcome" ? "Motyw powitalny" : occ.content?.title ?? "Komunikat";
    const custom = customThemes.find((c) => c.id === key);
    if (custom) return custom.name;
    return ALL_PHOTO_THEMES.find((t) => t.key === key)?.name ?? "—";
  };

  // Tło overlaya wg rodzaju motywu: przezroczysty = dawna maska przesłaniająca,
  // zdjęcie = obraz cover, gradient = tło CSS.
  const themeBg = (t: Theme): React.CSSProperties =>
    t.transparent
      ? { background: "rgba(25, 0, 100, 0.18)" }
      : t.image
        ? {
            backgroundImage: `url(${t.image})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }
        : { background: t.bg };

  // Tło: motyw okazjonalny (ręczny podgląd lub auto-należny) nadpisuje zwykły
  // wybór użytkownika; po zamknięciu/wyczerpaniu wraca zapamiętany wybór.
  const bgTheme: Theme = activeOccasional
    ? {
        key: activeOccasional.id,
        name: "",
        image: activeOccasional.image || undefined,
        bg: activeOccasional.bg,
      }
    : activeTheme;

  const overlayStyle: React.CSSProperties = {
    ...themeBg(bgTheme),
    ...(leftOffset != null ? { left: leftOffset } : {}),
  };

  const persistCustomThemes = (list: CustomTheme[]) => {
    try {
      localStorage.setItem(CUSTOM_THEMES_LS, JSON.stringify(list));
    } catch {
      /* obrazy mogły przekroczyć limit localStorage — pozostają w pamięci sesji */
    }
  };

  const moveTab = (id: PlannerTab, dir: -1 | 1) => {
    setTabOrder((prev) => {
      const idx = prev.indexOf(id);
      const next = [...prev];
      const swapIdx = idx + dir;
      if (swapIdx < 0 || swapIdx >= next.length) return prev;
      [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
      try { localStorage.setItem(TAB_ORDER_LS, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  // Wybór motywu = tylko PODGLĄD (nie utrwala, nie zamyka zasobnika).
  // Utrwalenie jako domyślny następuje osobno przez acceptAsDefault().
  const chooseTheme = (key: string) => {
    setThemeKey(key);
  };

  // Utrwalenie motywu jako domyślnego (podgląd + zapis do localStorage).
  const commitTheme = (key: string) => {
    setThemeKey(key);
    setDefaultThemeKey(key);
    try {
      localStorage.setItem(THEME_KEY_LS, key);
    } catch {
      /* brak dostępu do localStorage — ignorujemy */
    }
  };

  // Akceptacja aktualnie podglądanego motywu jako DOMYŚLNEGO — utrwala wybór.
  // Zasobnik pozostaje otwarty (zamykany „na życzenie").
  const acceptAsDefault = () => commitTheme(themeKey);
  // Czy podglądany motyw różni się od domyślnego (jest co zaakceptować).
  const previewDiffersFromDefault = themeKey !== defaultThemeKey;

  // Wyłączenie motywu (×) — szybki przełącznik: zapamiętujemy bieżący (do
  // przywrócenia) i od razu utrwalamy „przezroczysty" jako domyślny.
  const clearTheme = () => {
    if (themeKey !== "transparent") {
      setLastTheme(themeKey);
      try {
        localStorage.setItem(LAST_THEME_LS, themeKey);
      } catch {
        /* ignore */
      }
    }
    commitTheme("transparent");
  };
  // Przywrócenie ostatnio wyłączonego motywu (też utrwala jako domyślny).
  const restoreTheme = () => {
    if (lastTheme) commitTheme(lastTheme);
  };

  // ── Motywy „7 dni tygodnia" ───────────────────────────────────────────────
  const persistWeekdayThemes = (list: (WeekdayTheme | null)[]) => {
    try {
      localStorage.setItem(WEEKDAY_THEMES_LS, JSON.stringify(list));
    } catch {
      /* obrazy mogły przekroczyć limit localStorage — pozostają w pamięci sesji */
    }
  };
  // Dodanie obrazu dla wskazanego dnia (osobna operacja per dzień).
  const handleAddWeekdayTheme = async (dayIdx: number, file?: File) => {
    if (!file) return;
    const image = await downscaleImageToDataUrl(file);
    const name = file.name.replace(/\.[^.]+$/, "") || WEEKDAY_LABELS[dayIdx];
    setWeekdayThemes((list) => {
      const next = [...list];
      next[dayIdx] = { image, name };
      persistWeekdayThemes(next);
      return next;
    });
  };
  const removeWeekdayTheme = (dayIdx: number) => {
    setWeekdayThemes((list) => {
      const next = [...list];
      next[dayIdx] = null;
      persistWeekdayThemes(next);
      return next;
    });
    // Jeśli usuwany dzień był aktywnym pojedynczym motywem — wróć do przezroczystego.
    if (themeKey === `weekday-${dayIdx}`) chooseTheme("transparent");
  };
  const weekdayConfiguredCount = weekdayThemes.filter(Boolean).length;
  const weekdayAllSet = weekdayConfiguredCount === 7;

  // Dodanie własnego motywu do kolekcji (wczytany obraz jako data URL).
  const handleAddCustomTheme = async (file?: File) => {
    if (!file) return;
    const image = await downscaleImageToDataUrl(file);
    const id = `custom-${Date.now()}`;
    const baseName = file.name.replace(/\.[^.]+$/, "") || "Mój motyw";
    const theme: CustomTheme = { id, name: baseName, image };
    setCustomThemes((list) => {
      const next = [...list, theme];
      persistCustomThemes(next);
      return next;
    });
    chooseTheme(id);
  };

  // Usunięcie motywu własnego z kolekcji; jeśli był aktywny — powrót do „auto".
  const removeCustomTheme = (id: string) => {
    setCustomThemes((list) => {
      const next = list.filter((c) => c.id !== id);
      persistCustomThemes(next);
      return next;
    });
    if (themeKey === id) chooseTheme("transparent");
  };

  const toggleInSet = <T,>(setFn: React.Dispatch<React.SetStateAction<Set<T>>>, key: T) =>
    setFn((s) => {
      const n = new Set(s);
      n.has(key) ? n.delete(key) : n.add(key);
      return n;
    });

  // ── Terminarz: daty, wydarzenia, przenoszenie ─────────────────
  const todayISO = isoOf(new Date());
  const tomorrowISO = (() => {
    const t = new Date();
    t.setDate(t.getDate() + 1);
    return isoOf(t);
  })();
  const yesterdayISO = (() => {
    const t = new Date();
    t.setDate(t.getDate() - 1);
    return isoOf(t);
  })();
  // Kubełek prezentacji zadania: zaległe/wczoraj (data < dziś), dziś, jutro/dalej.
  const bucketOf = (date: string): Day =>
    date < todayISO ? "yesterday" : date === todayISO ? "today" : "tomorrow";
  // Domyślna data dla nowej pozycji dodanej do danej sekcji.
  const dateForDay = (day: Day): string =>
    day === "yesterday" ? yesterdayISO : day === "tomorrow" ? tomorrowISO : todayISO;
  // „Przenieś dalej": zaległe/wczoraj→dziś, dziś→jutro, jutro→z powrotem na dziś.
  const nextDateForMove = (date: string): string =>
    bucketOf(date) === "today" ? tomorrowISO : todayISO;
  const addDaysISO = (iso: string, n: number) => {
    const d = new Date(iso);
    d.setDate(d.getDate() + n);
    return isoOf(d);
  };
  // Nagłówek dnia: nazwa dnia + data w notacji „dzień miesiąc" (np. „18 czerwca").
  const dayHeaderParts = (iso: string) => {
    const d = new Date(iso);
    const weekday = d.toLocaleDateString("pl-PL", { weekday: "long" });
    return {
      weekday: weekday.charAt(0).toUpperCase() + weekday.slice(1),
      dm: d.toLocaleDateString("pl-PL", { day: "numeric", month: "long" }),
    };
  };
  const dayLabel = (iso: string) =>
    iso === todayISO
      ? "Dziś"
      : iso === tomorrowISO
        ? "Jutro"
        : new Date(iso).toLocaleDateString("pl-PL", { weekday: "long", day: "numeric", month: "long" });

  const [events, setEvents] = useState<Evt[]>([]);

  // Hydracja Terminarza z BE (raz) + zapis całości po zmianie (debounce).
  useEffect(() => {
    if (eventsHydratedRef.current || !eventsLoaded) return;
    const dtos = eventsResp?.data?.Events ?? [];
    setEvents(dtos.map(eventFromDto));
    bumpNextId(dtos.map((d) => d.LocalId));
    eventsHydratedRef.current = true;
  }, [eventsLoaded, eventsResp]);

  useEffect(() => {
    if (!eventsHydratedRef.current) return;
    const handle = setTimeout(() => {
      syncEventsMutation.mutate({ Events: events.map(eventToDto) });
    }, 800);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events]);

  const [moveId, setMoveId] = useState<number | null>(null);
  const [moveMode, setMoveMode] = useState<"manual" | "slots" | null>(null);
  const [mDate, setMDate] = useState("");
  const [mTime, setMTime] = useState("");

  // Data prezentowana w Terminarzu (nawigacja do dowolnego dnia, też przyszłego).
  const [viewDate, setViewDate] = useState<string>(() => isoOf(new Date()));
  const shiftViewDate = (delta: number) => {
    const d = new Date(viewDate);
    d.setDate(d.getDate() + delta);
    setViewDate(isoOf(d));
  };
  // Pełny opis bieżącego dnia (dla komponentu „Dziś" w nagłówku).
  const todayLong = new Date().toLocaleDateString("pl-PL", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  // ── Agenda Terminarza: kolejne dni z wydarzeniami od wybranej daty ─────────
  // Zasada: prezentujemy wydarzenia z następnych dni, ale tylko tyle dni, ile
  // w całości mieści się w dostępnej przestrzeni — bez przewijania. Dzień
  // wybrany pokazujemy zawsze (nawet pusty); kolejne dni tylko gdy mają
  // wydarzenia. Liczbę widocznych dni wyznacza pomiar wysokości (niżej).
  const agendaCandidates: { date: string; list: Evt[] }[] = [];
  for (let i = 0; i < 30; i++) {
    const d = addDaysISO(viewDate, i);
    const list = events
      .filter((e) => e.date === d && !e.deleted)
      .sort((a, b) => toMin(a.time) - toMin(b.time));
    if (i === 0 || list.length > 0) agendaCandidates.push({ date: d, list });
  }

  const calBodyRef = useRef<HTMLDivElement>(null);
  const agendaRef = useRef<HTMLDivElement>(null);
  const dayBlockRefs = useRef<(HTMLDivElement | null)[]>([]);
  // Wysokość, do jakiej przyciąć agendę, by zmieściły się tylko całe dni (bez scrolla).
  const [agendaMaxHeight, setAgendaMaxHeight] = useState<number | null>(null);

  // Rejestracja nowego terminu: typy wydarzeń (uzupełnialne) + karta + sloty.
  const [evtTypes, setEvtTypes] = useState<EvtType[]>(EVT_TYPES_INIT);
  const [evtForm, setEvtForm] = useState<EvtForm | null>(null);
  const [evtSlotsOpen, setEvtSlotsOpen] = useState(false);
  const [typeMenuOpen, setTypeMenuOpen] = useState(false);
  const [caseMenuOpen, setCaseMenuOpen] = useState(false);
  // Zapytanie do wyszukiwania teczek — odłożone (debounce), by nie odpytywać co znak.
  const [caseSearch, setCaseSearch] = useState("");
  // Błąd walidacji formularza terminu: które pole uzupełnić + komunikat.
  const [evtError, setEvtError] = useState<{ field: "title" | "date" | "time"; msg: string } | null>(null);

  // Pomiar agendy: ile kolejnych dni mieści się w całości (bez przewijania).
  // Po deklaracji evtForm — jego obecność (karta dodawania) zmienia dostępną wysokość.
  useLayoutEffect(() => {
    if (tab !== "cal") return;
    const body = calBodyRef.current;
    const agenda = agendaRef.current;
    if (!body || !agenda) return;
    let raf = 0;
    let last: number | null | undefined;
    const apply = () => {
      const availBottom = body.getBoundingClientRect().bottom;
      const agendaTop = agenda.getBoundingClientRect().top;
      // Wypełniamy listę tylko do 90% dostępnej wysokości — reszta zostaje pusta.
      const limitBottom = agendaTop + (availBottom - agendaTop) * 0.9;
      let fit = 0;
      const blocks = dayBlockRefs.current.slice(0, agendaCandidates.length);
      for (const el of blocks) {
        if (!el) break;
        const rect = el.getBoundingClientRect();
        if (rect.bottom <= limitBottom) fit = rect.bottom - agendaTop;
        else break;
      }
      const next = fit > 0 ? Math.ceil(fit) : null;
      if (next !== last) {
        last = next;
        setAgendaMaxHeight(next);
      }
    };
    apply(); // pierwszy pomiar synchronicznie (bez migotania)
    // Pomiary z ResizeObserver odkładamy do następnej klatki — łamie pętlę
    // sprzężenia RO (zapis maxHeight zmienia layout, który RO znów obserwuje).
    const onObserve = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(apply);
    };
    const ro = new ResizeObserver(onObserve);
    ro.observe(body);
    window.addEventListener("resize", onObserve);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener("resize", onObserve);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, open, viewDate, events, evtForm, agendaCandidates.length]);

  const openManual = (e: Evt) => { setMoveId(e.id); setMoveMode("manual"); setMDate(e.date); setMTime(e.time); };
  const openSlots = (e: Evt) => { setMoveId(e.id); setMoveMode("slots"); };
  const cancelMove = () => { setMoveId(null); setMoveMode(null); };
  const applyManual = () => {
    if (moveId == null || !mDate || !mTime) return;
    setEvents((evs) => evs.map((e) => (e.id === moveId ? { ...e, date: mDate, time: mTime } : e)));
    cancelMove();
  };
  const pickSlot = (date: string, time: string) => {
    setEvents((evs) => evs.map((e) => (e.id === moveId ? { ...e, date, time } : e)));
    cancelMove();
  };

  // Kosz Terminarza: soft-delete do sekcji „Usunięte", przywracanie, czyszczenie.
  const deleteEvent = (id: number) => {
    setEvents((evs) => evs.map((e) => (e.id === id ? { ...e, deleted: true } : e)));
    if (moveId === id) cancelMove();
  };
  const restoreEvent = (id: number) =>
    setEvents((evs) => evs.map((e) => (e.id === id ? { ...e, deleted: false } : e)));
  const clearDeletedEvents = () => setEvents((evs) => evs.filter((e) => !e.deleted));
  const deletedEvents = events.filter((e) => e.deleted);

  // Wolne sloty (Dziś/Jutro, 08:00–18:00) mieszczące wskazany czas trwania.
  const freeSlotsForDur = (dur: number, excludeId?: number) => {
    const out: { date: string; time: string }[] = [];
    for (const d of [todayISO, tomorrowISO]) {
      const occ = events
        .filter((e) => e.id !== excludeId && e.date === d && !e.deleted)
        .map((e) => [toMin(e.time), toMin(e.time) + e.dur] as [number, number]);
      for (let s = WORK_START; s + dur <= WORK_END; s += SLOT_STEP) {
        const e2 = s + dur;
        if (!occ.some(([os, oe]) => s < oe && e2 > os)) out.push({ date: d, time: toHHMM(s) });
      }
    }
    return out;
  };
  const freeSlots = (evt: Evt) => freeSlotsForDur(evt.dur, evt.id);

  // ── Rejestracja nowego terminu ────────────────────────────────────
  const openEvtForm = () => {
    // Domyślnie data nowego terminu = aktualnie przeglądany dzień.
    setEvtForm({ ...EMPTY_EVT, date: viewDate });
    setEvtSlotsOpen(false);
    setTypeMenuOpen(false);
    setCaseMenuOpen(false);
    setEvtError(null);
  };
  const cancelEvtForm = () => {
    setEvtForm(null);
    setEvtSlotsOpen(false);
    setTypeMenuOpen(false);
    setCaseMenuOpen(false);
    setEvtError(null);
  };
  const pickType = (t: EvtType) => {
    setEvtForm((f) => (f ? { ...f, typeId: t.id, typeName: t.name } : f));
    setTypeMenuOpen(false); // po wyborze chowamy dropdown
  };
  // Uzupełnienie listy typów nowym wpisem (z karty).
  const addType = (name: string) => {
    const clean = name.trim();
    if (!clean) return;
    const accent = TYPE_PALETTE[evtTypes.length % TYPE_PALETTE.length];
    const t: EvtType = { id: nextId.current++, name: clean, accent };
    setEvtTypes((ts) => [...ts, t]);
    setEvtForm((f) => (f ? { ...f, typeId: t.id, typeName: clean } : f));
    setTypeMenuOpen(false); // po dodaniu i załadowaniu nowego typu chowamy dropdown
  };
  const pickEvtSlot = (date: string, time: string) => {
    setEvtForm((f) => (f ? { ...f, date, time } : f));
    setEvtSlotsOpen(false);
    setEvtError(null);
  };
  const saveEvent = () => {
    if (!evtForm) return;
    const title = evtForm.title.trim();
    // Walidacja z komunikatem wskazującym pole do uzupełnienia (zamiast cichego return).
    if (!title) {
      setEvtError({ field: "title", msg: "Podaj tytuł wydarzenia." });
      return;
    }
    if (!evtForm.date) {
      setEvtError({ field: "date", msg: "Wybierz datę wydarzenia." });
      return;
    }
    // type="time" zwraca pusty string przy niepełnej godzinie (np. brak minut).
    if (!/^\d{2}:\d{2}$/.test(evtForm.time)) {
      setEvtError({ field: "time", msg: "Uzupełnij godzinę w formacie GG:MM (np. 09:30)." });
      return;
    }
    setEvtError(null);
    const type = evtTypes.find((t) => t.id === evtForm.typeId);
    const savedDate = evtForm.date;
    setEvents((evs) => [
      ...evs,
      {
        id: nextId.current++,
        date: savedDate,
        time: evtForm.time,
        dur: evtForm.dur,
        title,
        sub: evtForm.sub.trim(),
        accent: type?.accent ?? "#5d49c7",
        type: type?.name,
        caseId: evtForm.caseId || undefined,
        caseName: evtForm.caseId ? evtForm.caseName.trim() : undefined,
      },
    ]);
    // Przeskocz agendę na dzień zapisanego terminu, by był od razu widoczny
    // (agenda pokazuje dni od viewDate; przyszłe terminy spoza okna były niewidoczne).
    setViewDate(savedDate);
    cancelEvtForm();
  };

  // Podpowiedzi typów wg wpisywanej nazwy + sygnał o możliwości dodania nowego.
  const typeQuery = evtForm?.typeName.trim() ?? "";
  const typeSuggestions = typeQuery
    ? evtTypes.filter((t) => t.name.toLowerCase().includes(typeQuery.toLowerCase()))
    : evtTypes;
  const canAddType =
    typeQuery.length > 0 &&
    !evtTypes.some((t) => t.name.toLowerCase() === typeQuery.toLowerCase());

  // ── Wyszukiwanie i wiązanie teczki (sprawy) ───────────────────────
  const caseQuery = evtForm?.caseName.trim() ?? "";
  // Debounce wpisywanego tekstu, by ograniczyć liczbę zapytań do BE.
  useEffect(() => {
    const h = setTimeout(() => setCaseSearch(caseQuery), 300);
    return () => clearTimeout(h);
  }, [caseQuery]);
  // Pobieramy teczki tylko gdy formularz otwarty, menu aktywne i jest co najmniej 2 znaki.
  const caseSearchEnabled = !!evtForm && caseMenuOpen && caseSearch.length >= 2;
  const { data: caseResp, isFetching: caseLoading } = casesService.useGetSimpleCases(
    { text: caseSearch },
    caseSearchEnabled,
  );
  // /cases/simple zwraca { Result: { Items: [...] } } — nie gołą tablicę
  // (typ w serwisie jest zbyt luźny; tak czytają to też SearchCases*).
  const caseList =
    (caseResp?.data as unknown as { Result?: { Items?: CaseHit[] } } | undefined)
      ?.Result?.Items ?? [];
  const caseSuggestions = caseList
    .filter((c) => !evtForm?.caseId || c.Id !== evtForm.caseId)
    .slice(0, 6);
  const pickCase = (c: CaseHit) => {
    const label = [c.Signature, c.Title].filter(Boolean).join(" — ");
    setEvtForm((f) => (f ? { ...f, caseId: c.Id, caseName: label } : f));
    setCaseMenuOpen(false);
  };
  const clearCase = () =>
    setEvtForm((f) => (f ? { ...f, caseId: "", caseName: "" } : f));

  const update = (id: number, patch: Partial<Todo>) =>
    setTodos((t) => mapTree(t, id, (x) => ({ ...x, ...patch })));
  const toggle = (id: number) =>
    setTodos((t) => mapTree(t, id, (x) => ({ ...x, done: !x.done })));
  const togglePin = (id: number) =>
    setTodos((t) => mapTree(t, id, (x) => ({ ...x, pin: !x.pin })));
  const remove = (id: number) => setTodos((t) => removeInTree(t, id));

  // Dodanie nowej, pustej pozycji do wybranej sekcji (Dziś/Jutro) — od razu w edycji.
  const addTopForDay = (day: Day) => {
    const item: Todo = { id: nextId.current++, text: "", done: false, pin: false, date: dateForDay(day), children: [] };
    setTodos((t) => [...t, item]);
    setCollapsedSections((s) => {
      const n = new Set(s);
      n.delete(day); // rozwiń sekcję, by widać było nową pozycję
      return n;
    });
    setEditingId(item.id);
    setEditDraft("");
  };

  // Dodanie podzadania z poziomu rodzica — od razu w trybie edycji.
  const addChild = (parentId: number, date: string) => {
    const child: Todo = { id: nextId.current++, text: "", done: false, pin: false, date, children: [] };
    setTodos((t) => mapTree(t, parentId, (p) => ({ ...p, children: [...p.children, child] })));
    setEditingId(child.id);
    setEditDraft("");
  };

  // Zmiana kolejności rodzeństwa (w obrębie tej samej sekcji dla węzłów najwyższego poziomu).
  const moveTop = (id: number, dir: number) =>
    setTodos((list) => {
      const idx = list.findIndex((x) => x.id === id);
      if (idx < 0) return list;
      const it = list[idx];
      let j = idx + dir;
      while (j >= 0 && j < list.length && !(bucketOf(list[j].date) === bucketOf(it.date) && list[j].done === it.done)) j += dir;
      if (j < 0 || j >= list.length) return list;
      const n = [...list];
      [n[idx], n[j]] = [n[j], n[idx]];
      return n;
    });
  const moveChild = (parentId: number, id: number, dir: number) =>
    setTodos((t) => mapTree(t, parentId, (p) => ({ ...p, children: swapAdjacent(p.children, id, dir) })));

  // Przeniesienie pojedynczego zadania między dniami: Wczoraj→Dziś, Dziś→Jutro,
  // Jutro→Dziś (powrót). Krok po kroku.
  const moveDay = (id: number) =>
    setTodos((t) => mapTree(t, id, (x) => ({ ...x, date: nextDateForMove(x.date) })));

  // Przeniesienie WSZYSTKICH niewykonanych zadań sekcji na następny dzień
  // (Wczoraj→Dziś, Dziś→Jutro) — z przycisku w nagłówku sekcji.
  const moveAllToNextDay = (day: Day) =>
    setTodos((list) =>
      list.map((t) => (bucketOf(t.date) === day && !t.done ? { ...t, date: nextDateForMove(t.date) } : t)));

  // ── „Skontaktuj się": karta kontaktu + zapis do bazy Podmioty ─────
  const openContactForm = () => {
    setContactForm({ ...EMPTY_CONTACT });
    setPurposeMenuOpen(false);
    setCollapsedSections((s) => {
      const n = new Set(s);
      n.delete("contact"); // rozwiń sekcję, by widać było kartę
      return n;
    });
  };
  const cancelContactForm = () => {
    setContactForm(null);
    setPurposeMenuOpen(false);
  };
  const pickPodmiot = (p: Podmiot) =>
    setContactForm((f) => (f ? { ...f, name: p.name, phone: p.phone, email: p.email } : f));
  const pickPurpose = (val: string) => {
    setContactForm((f) => (f ? { ...f, purpose: val } : f));
    setPurposeMenuOpen(false); // po wyborze chowamy podpowiedzi
  };
  const saveContactForm = () => {
    if (!contactForm) return;
    const name = contactForm.name.trim();
    if (!name) return; // nazwa to minimum wymagane
    const phone = contactForm.phone.trim();
    const email = contactForm.email.trim();
    const purpose = contactForm.purpose.trim();
    setContacts((list) => [
      ...list,
      {
        id: nextId.current++,
        name,
        purpose,
        phone,
        email,
        date: contactForm.date,
        time: contactForm.time,
        done: false,
      },
    ]);
    // zapis nowego podmiotu do bazy „Podmioty" (jeśli jeszcze go nie ma — wg nazwy)
    setPodmioty((base) =>
      base.some((p) => p.name.toLowerCase() === name.toLowerCase())
        ? base
        : [...base, { id: nextId.current++, name, phone, email }],
    );
    // zapamiętanie celu kontaktu do podpowiedzi w przyszłości (bez duplikatów)
    if (purpose) {
      setPurposes((list) =>
        list.some((pp) => pp.toLowerCase() === purpose.toLowerCase()) ? list : [purpose, ...list],
      );
    }
    setContactForm(null);
    setPurposeMenuOpen(false);
  };
  const removeContact = (id: number) =>
    setContacts((list) => list.filter((c) => c.id !== id));
  const toggleContactDone = (id: number) =>
    setContacts((list) => list.map((c) => (c.id === id ? { ...c, done: !c.done } : c)));

  // Podgląd treści notatki w tooltipie. Treść z RichEditora to HTML — jeśli zawiera
  // rozbudowane elementy (tabela/obraz/itp.) lub jest zbyt długa, pokazujemy komunikat.
  const noteTooltip = (note: { title: string; content: string }) => {
    const html = note.content || "";
    if (/<(table|img|iframe|figure|video|thead|tbody)\b/i.test(html)) {
      return "Notatka zawiera rozbudowaną treść (np. tabelę) i nie nadaje się do podglądu w podpowiedzi — kliknij, aby otworzyć.";
    }
    const text = html
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .replace(/&amp;/gi, "&")
      .replace(/\s+/g, " ")
      .trim();
    if (text.length > 280) {
      return "Notatka jest zbyt długa, by pokazać ją w podpowiedzi — kliknij, aby otworzyć.";
    }
    const head = note.title ? note.title.trim() : "";
    return [head, text].filter(Boolean).join("\n") || "Notatka (pusta)";
  };

  // ── Notatka kontaktu (formatka AddNote) ───────────────────────────
  const openNote = (id: number) => setNoteContactId(id);
  const closeNote = () => setNoteContactId(null);
  // AddNote woła addNote tylko przy TWORZENIU — zapamiętujemy referencję
  // notatki (id/tytuł/treść), by pokazać ikonę i umożliwić późniejszą edycję.
  const onNoteCreated = (contactId: number, n: INote) =>
    setContacts((list) =>
      list.map((c) =>
        c.id === contactId ? { ...c, note: { id: n.Id, title: n.Title, content: n.Content } } : c,
      ),
    );
  const noteContact = noteContactId != null ? contacts.find((c) => c.id === noteContactId) : null;

  // Podpowiedzi z bazy Podmioty wg wpisywanej nazwy (maks. 4).
  const suggestions =
    contactForm && contactForm.name.trim()
      ? podmioty
          .filter(
            (p) =>
              p.name.toLowerCase().includes(contactForm.name.trim().toLowerCase()) &&
              p.name.toLowerCase() !== contactForm.name.trim().toLowerCase(),
          )
          .slice(0, 4)
      : [];

  // Podpowiedzi celu kontaktu z wcześniej zapisanych wpisów (po fokusie wszystkie,
  // w trakcie pisania — filtrowane; pomijamy dokładne trafienie).
  const purposeQuery = contactForm?.purpose.trim() ?? "";
  const purposeSuggestions = (
    purposeQuery
      ? purposes.filter((pp) => pp.toLowerCase().includes(purposeQuery.toLowerCase()))
      : purposes
  )
    .filter((pp) => pp.toLowerCase() !== purposeQuery.toLowerCase())
    .slice(0, 5);

  const startEdit = (t: Todo) => {
    setEditingId(t.id);
    setEditDraft(t.text);
  };
  const commitEdit = () => {
    if (editingId == null) return;
    const text = editDraft.trim();
    if (!text) remove(editingId);
    else update(editingId, { text });
    setEditingId(null);
  };
  const cancelEdit = () => {
    if (editingId != null) {
      const node = flatten(todos).find((x) => x.id === editingId);
      if (node && !node.text.trim()) remove(editingId); // porzucone nowe (puste) podzadanie
    }
    setEditingId(null);
  };

  const renderTask = (t: Todo, parentId: number | null) => {
    const hasKids = t.children.length > 0;
    const kidsHidden = collapsedKids.has(t.id);
    return (
      <div key={t.id}>
        <div className={`${styles.todoItem} ${t.done ? styles.todoDone : ""} ${editingId === t.id ? styles.editing : ""}`}>
          {hasKids ? (
            <button
              className={styles.todoKidsToggle}
              title={kidsHidden ? "Pokaż podzadania" : "Ukryj podzadania"}
              onClick={() => toggleInSet(setCollapsedKids, t.id)}
            >
              <FontAwesomeIcon icon={kidsHidden ? faChevronRight : faChevronDown} />
            </button>
          ) : (
            <span className={styles.todoKidsSpacer} />
          )}
          <input type="checkbox" checked={t.done} onChange={() => toggle(t.id)} />
          {editingId === t.id ? (
            <textarea
              className={styles.todoEdit}
              autoFocus
              value={editDraft}
              onChange={(e) => setEditDraft(e.target.value)}
              onBlur={commitEdit}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  commitEdit();
                }
                if (e.key === "Escape") cancelEdit();
              }}
            />
          ) : (
            <span
              className={styles.todoText}
              onClick={() => toggle(t.id)}
              onDoubleClick={() => startEdit(t)}
              title="Dwuklik = edytuj"
            >
              {t.text}
            </span>
          )}
          <div className={styles.todoActions}>
            {!t.done && parentId === null && (
              <button
                className={`${styles.todoIconBtn} ${styles.todoDayBtn}`}
                title={bucketOf(t.date) === "today" ? "Przenieś na jutro" : "Przenieś na dziś"}
                onClick={() => moveDay(t.id)}
              >
                <FontAwesomeIcon icon={bucketOf(t.date) === "tomorrow" ? faArrowLeft : faArrowRight} />
              </button>
            )}
            {!t.done && (
              <>
                <button className={styles.todoIconBtn} title="W górę"
                  onClick={() => (parentId ? moveChild(parentId, t.id, -1) : moveTop(t.id, -1))}>
                  <FontAwesomeIcon icon={faChevronUp} />
                </button>
                <button className={styles.todoIconBtn} title="W dół"
                  onClick={() => (parentId ? moveChild(parentId, t.id, 1) : moveTop(t.id, 1))}>
                  <FontAwesomeIcon icon={faChevronDown} />
                </button>
                <button className={styles.todoIconBtn} title="Dodaj podzadanie"
                  onClick={() => addChild(t.id, t.date)}>
                  <FontAwesomeIcon icon={faPlus} />
                </button>
              </>
            )}
            <button className={styles.todoIconBtn} title="Edytuj" onClick={() => startEdit(t)}>
              <FontAwesomeIcon icon={faPen} />
            </button>
            <button className={`${styles.todoIconBtn} ${t.pin ? styles.todoPinned : ""}`}
              title={t.pin ? "Odepnij" : "Przypnij"} onClick={() => togglePin(t.id)}>
              <FontAwesomeIcon icon={faThumbtack} />
            </button>
            <button className={styles.todoIconBtn} title="Usuń" onClick={() => remove(t.id)}>
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
        </div>
        {hasKids && !kidsHidden && (
          <div className={styles.todoChildren}>
            {t.children.map((c) => renderTask(c, t.id))}
          </div>
        )}
      </div>
    );
  };

  // Sekcja z możliwością zwijania/rozwijania; Dziś/Jutro pokazujemy zawsze
  // (alwaysShow), by można było pojedynczo przenosić zadania między dniami.
  const section = (
    key: string,
    label: string,
    list: Todo[],
    alwaysShow = false,
    addDay?: Day,
    moveAllDay?: Day,
  ) => {
    if (list.length === 0 && !alwaysShow) return null;
    const collapsed = collapsedSections.has(key);
    return (
      <div className={styles.todoGroup}>
        <div
          className={styles.todoDay}
          onClick={() => toggleInSet(setCollapsedSections, key)}
          style={{ cursor: "pointer" }}
        >
          <FontAwesomeIcon
            className={styles.todoSectionChevron}
            icon={collapsed ? faChevronRight : faChevronDown}
          />
          {label}
          <span className={styles.todoSectionCount}>{list.length}</span>
          {(moveAllDay || addDay) && (
            <div className={styles.todoHeadActions}>
              {moveAllDay && (
                <button
                  className={styles.todoMoveBtn2}
                  title="Przenieś na następny dzień"
                  onClick={(e) => {
                    e.stopPropagation();
                    moveAllToNextDay(moveAllDay);
                  }}
                >
                  <FontAwesomeIcon icon={faArrowRight} />
                </button>
              )}
              {addDay && (
                <button
                  className={styles.todoAddBtn2}
                  title="Dodaj zadanie"
                  onClick={(e) => {
                    e.stopPropagation();
                    addTopForDay(addDay);
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} />
                </button>
              )}
            </div>
          )}
        </div>
        {!collapsed &&
          (list.length > 0 ? (
            list.map((t) => renderTask(t, null))
          ) : (
            <div className={styles.todoEmpty}>Brak zadań</div>
          ))}
      </div>
    );
  };

  const yesterday = todos.filter((t) => bucketOf(t.date) === "yesterday" && !t.done);
  const today = todos.filter((t) => bucketOf(t.date) === "today" && !t.done);
  const tomorrow = todos.filter((t) => bucketOf(t.date) === "tomorrow" && !t.done);
  const done = todos.filter((t) => t.done);
  const activeContacts = contacts.filter((c) => !c.done);
  const doneContacts = contacts.filter((c) => c.done);

  // Renderowanie pozycji kontaktu (używane w „Skontaktuj się" i w „Wykonane").
  const renderContact = (c: ContactTask) => (
    <div key={c.id} className={`${styles.cItem} ${c.done ? styles.cItemDone : ""}`}>
      <input
        type="checkbox"
        className={styles.cItemCheck}
        checked={c.done}
        onChange={() => toggleContactDone(c.id)}
        title={c.done ? "Oznacz jako niewykonane" : "Oznacz jako wykonane"}
      />
      <div className={styles.cItemGrid}>
        <div className={styles.cItemCol}>
          <span className={styles.cItemName}>{c.name}</span>
          {c.purpose && <span className={styles.cItemPurpose}>{c.purpose}</span>}
        </div>
        <div className={styles.cItemCol}>
          {(c.date || c.time) && (
            <span className={styles.cItemLine}>
              <FontAwesomeIcon icon={faClock} />
              {c.date ? dayLabel(c.date) : ""} {c.time}
            </span>
          )}
          {c.phone && (
            <span className={styles.cItemLine}>
              <FontAwesomeIcon icon={faPhone} />
              {c.phone}
            </span>
          )}
        </div>
      </div>
      <div className={styles.cItemActions}>
        <button
          className={`${styles.cItemNote} ${c.note ? styles.cItemNoteOn : ""}`}
          title={c.note ? noteTooltip(c.note) : "Dodaj notatkę"}
          onClick={() => openNote(c.id)}
        >
          <FontAwesomeIcon icon={faStickyNote} />
        </button>
        <button className={styles.cItemDel} title="Usuń" onClick={() => removeContact(c.id)}>
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>
    </div>
  );

  // Pojedyncze wydarzenie Terminarza (kafelek + panele przenoszenia).
  const renderEvent = (ev: Evt) => (
    <div key={ev.id}>
      <div className={styles.calItem} style={{ ["--accent" as string]: ev.accent }}>
        <span className={styles.calTime}>
          <FontAwesomeIcon icon={faClock} /> {ev.time}
        </span>
        <span className={styles.calText}>
          <span className={styles.calTitle}>{ev.title}</span>
          <span className={styles.calSub}>
            {[ev.type, ev.sub].filter(Boolean).join(" · ")}
            {(ev.type || ev.sub) && " · "}{ev.dur} min
          </span>
        </span>
        <div className={styles.calActions}>
          <button className={styles.calIconBtn} title="Przenieś (data i godzina)" onClick={() => openManual(ev)}>
            <FontAwesomeIcon icon={faPen} />
          </button>
          <button className={styles.calIconBtn} title="Znajdź wolny termin" onClick={() => openSlots(ev)}>
            <FontAwesomeIcon icon={faMagnifyingGlass} />
          </button>
          <button className={styles.calIconBtn} title="Usuń termin" onClick={() => deleteEvent(ev.id)}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      </div>

      {moveId === ev.id && moveMode === "manual" && (
        <div className={styles.moveBox}>
          <div className={styles.moveTitle}>Przenieś na wybraną datę i godzinę</div>
          <div className={styles.moveRow}>
            <input type="date" className={styles.moveInput} value={mDate} onChange={(e) => setMDate(e.target.value)} />
            <input type="time" className={styles.moveInput} value={mTime} onChange={(e) => setMTime(e.target.value)} />
          </div>
          <div className={styles.moveBtns}>
            <button className={styles.moveCancel} onClick={cancelMove}>Anuluj</button>
            <button className={styles.moveApply} onClick={applyManual}>Przenieś</button>
          </div>
        </div>
      )}

      {moveId === ev.id && moveMode === "slots" && (
        <div className={styles.moveBox}>
          <div className={styles.moveTitle}>
            Wolne terminy ({ev.dur} min) — wybierz, aby przenieść
          </div>
          <div className={styles.slotsList}>
            {freeSlots(ev).map((s, i) => (
              <button key={i} className={styles.slotBtn} onClick={() => pickSlot(s.date, s.time)}>
                {dayLabel(s.date)} · {s.time}
              </button>
            ))}
          </div>
          <div className={styles.moveBtns}>
            <button className={styles.moveCancel} onClick={cancelMove}>Anuluj</button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div
      ref={overlayRef}
      className={`${styles.plannerOverlay} ${open ? styles.plannerOpen : ""}`}
      style={overlayStyle}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Kontrolka motywu tła (lewy obszar — zamiast zwykłej maski). */}
      <div
        className={styles.themeControl}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className={styles.themeBtn}
          onClick={openThemeMenu}
          title="Motyw tła"
        >
          <FontAwesomeIcon icon={faImage} />
          <span>Motyw</span>
        </button>
        {/* Wyłącz motyw (×) / przywróć ostatni (↺) — bez zamykania slajdu Terminarz. */}
        {themeKey !== "transparent" ? (
          <button
            className={styles.themeClearBtn}
            title="Wyłącz motyw (przezroczyste tło)"
            onClick={clearTheme}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        ) : (
          <button
            className={styles.themeClearBtn}
            title={lastTheme ? "Przywróć poprzedni motyw" : "Brak motywu do przywrócenia"}
            disabled={!lastTheme}
            onClick={restoreTheme}
          >
            <FontAwesomeIcon icon={faRotateLeft} />
          </button>
        )}
        {themeMenuOpen && (
          <div className={styles.themeMenu}>
            {/* Nagłówek: dodaj własny motyw + zamknij (zasobnik zamykany na życzenie). */}
            <div className={styles.themeMenuHeader}>
              <button
                className={styles.themeAdd}
                onClick={() => themeFileRef.current?.click()}
              >
                <FontAwesomeIcon icon={faPlus} /> Dodaj własny motyw…
              </button>
              <button
                className={styles.themeMenuClose}
                title="Zamknij"
                onClick={() => setThemeMenuOpen(false)}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>

            <div className={styles.themeMenuScroll}>
              {/* Przezroczysty — baza/domyślny, zawsze widoczny (poza grupami). */}
              <button
                className={`${styles.themeOpt} ${themeKey === "transparent" ? styles.themeOptActive : ""}`}
                onClick={() => chooseTheme("transparent")}
              >
                <span className={styles.themeSwatch} style={swatchStyle(transparentTheme)} />
                <span>{transparentTheme.name}</span>
              </button>

              {/* Grupa „Moje motywy" */}
              <button
                className={styles.themeGroupHead}
                onClick={() => toggleInSet(setCollapsedThemeGroups, "custom")}
              >
                <FontAwesomeIcon
                  className={styles.todoSectionChevron}
                  icon={collapsedThemeGroups.has("custom") ? faChevronRight : faChevronDown}
                />
                Moje motywy
                <span className={styles.todoSectionCount}>{customThemes.length}</span>
              </button>
              {!collapsedThemeGroups.has("custom") &&
                (customThemes.length > 0 ? (
                  customThemes.map((c) => (
                    <div
                      key={c.id}
                      className={`${styles.themeOpt} ${themeKey === c.id ? styles.themeOptActive : ""}`}
                      onClick={() => chooseTheme(c.id)}
                    >
                      <span
                        className={styles.themeSwatch}
                        style={{ backgroundImage: `url(${c.image})`, backgroundSize: "cover" }}
                      />
                      <span className={styles.themeOptName}>{c.name}</span>
                      <button
                        className={styles.themeDel}
                        title="Usuń motyw"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeCustomTheme(c.id);
                        }}
                      >
                        <FontAwesomeIcon icon={faXmark} />
                      </button>
                    </div>
                  ))
                ) : (
                  <div className={styles.themeEmpty}>Brak własnych motywów</div>
                ))}

              {/* Grupa „7 dni tygodnia" — motyw osobno per dzień + codzienna zmiana */}
              <button
                className={styles.themeGroupHead}
                onClick={() => toggleInSet(setCollapsedThemeGroups, "weekday")}
              >
                <FontAwesomeIcon
                  className={styles.todoSectionChevron}
                  icon={collapsedThemeGroups.has("weekday") ? faChevronRight : faChevronDown}
                />
                7 dni tygodnia
                <span className={styles.todoSectionCount}>{weekdayConfiguredCount}/7</span>
              </button>
              {!collapsedThemeGroups.has("weekday") && (
                <>
                  {WEEKDAY_ORDER.map((dayIdx) => {
                    const wd = weekdayThemes[dayIdx];
                    // Wiersz dnia jest klikalny (wybór pojedynczego motywu) tylko gdy
                    // ma przypisany obraz — bez uruchamiania codziennej zmiany.
                    return (
                      <div
                        key={dayIdx}
                        className={`${styles.themeOpt} ${themeKey === `weekday-${dayIdx}` ? styles.themeOptActive : ""}`}
                        onClick={() => wd && chooseTheme(`weekday-${dayIdx}`)}
                        style={wd ? undefined : { cursor: "default" }}
                      >
                        <span
                          className={styles.themeSwatch}
                          style={
                            wd
                              ? { backgroundImage: `url(${wd.image})`, backgroundSize: "cover" }
                              : swatchStyle(transparentTheme)
                          }
                        />
                        <span className={styles.themeOptName}>{WEEKDAY_LABELS[dayIdx]}</span>
                        {wd ? (
                          <button
                            className={styles.themeDel}
                            title="Usuń motyw tego dnia"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeWeekdayTheme(dayIdx);
                            }}
                          >
                            <FontAwesomeIcon icon={faXmark} />
                          </button>
                        ) : (
                          <button
                            className={styles.themeDel}
                            title="Dodaj motyw tego dnia"
                            onClick={(e) => {
                              e.stopPropagation();
                              weekdayTargetRef.current = dayIdx;
                              weekdayFileRef.current?.click();
                            }}
                          >
                            <FontAwesomeIcon icon={faPlus} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                  <button
                    className={styles.themeRunBtn}
                    // Aktywny dopiero po dodaniu motywu dla każdego dnia; w trybie
                    // aktywnym zawsze klikalny (możliwość wyłączenia).
                    disabled={themeKey !== "weekday" && !weekdayAllSet}
                    onClick={() =>
                      chooseTheme(themeKey === "weekday" ? "transparent" : "weekday")
                    }
                  >
                    {themeKey === "weekday"
                      ? "Codzienna zmiana aktywna — wyłącz"
                      : "Uruchom codzienną zmianę motywu"}
                  </button>
                  {!weekdayAllSet && themeKey !== "weekday" && (
                    <div className={styles.themeEmpty}>
                      Dodaj motyw dla każdego dnia, aby uruchomić codzienną zmianę.
                    </div>
                  )}
                </>
              )}

              {/* Grupy kategorii: Pory roku, Pejzaż, Kontynent, Kraj, Miasto, Fauna, Flora */}
              {CATEGORY_GROUPS.map((g) => (
                <Fragment key={g.key}>
                  <button
                    className={styles.themeGroupHead}
                    onClick={() => toggleInSet(setCollapsedThemeGroups, g.key)}
                  >
                    <FontAwesomeIcon
                      className={styles.todoSectionChevron}
                      icon={collapsedThemeGroups.has(g.key) ? faChevronRight : faChevronDown}
                    />
                    {g.title}
                    <span className={styles.todoSectionCount}>{g.themes.length}</span>
                  </button>
                  {!collapsedThemeGroups.has(g.key) &&
                    g.themes.map((t) => (
                      <button
                        key={t.key}
                        className={`${styles.themeOpt} ${themeKey === t.key ? styles.themeOptActive : ""}`}
                        onClick={() => chooseTheme(t.key)}
                      >
                        <span className={styles.themeSwatch} style={swatchStyle(t)} />
                        <span className={styles.themeOptName}>{t.name}</span>
                      </button>
                    ))}
                </Fragment>
              ))}

              {/* Grupa „Motywy okazjonalne" — kampanie operatora do podglądu na żądanie. */}
              <button
                className={styles.themeGroupHead}
                onClick={() => toggleInSet(setCollapsedThemeGroups, "occasional")}
              >
                <FontAwesomeIcon
                  className={styles.todoSectionChevron}
                  icon={collapsedThemeGroups.has("occasional") ? faChevronRight : faChevronDown}
                />
                Motywy okazjonalne
                <span className={styles.todoSectionCount}>{OCCASIONAL_THEMES.length}</span>
              </button>
              {!collapsedThemeGroups.has("occasional") &&
                OCCASIONAL_THEMES.map((c) => (
                  <button
                    key={c.id}
                    className={`${styles.themeOpt} ${themeKey === c.id ? styles.themeOptActive : ""}`}
                    onClick={() => chooseTheme(c.id)}
                  >
                    <span
                      className={styles.themeSwatch}
                      style={
                        c.image
                          ? { backgroundImage: `url(${c.image})`, backgroundSize: "cover" }
                          : { background: c.bg }
                      }
                    />
                    <span className={styles.themeOptName}>
                      {c.kind === "welcome"
                        ? "Motyw powitalny"
                        : c.content?.title ?? "Komunikat"}
                    </span>
                  </button>
                ))}
            </div>

            {/* Pasek akceptacji: utrwala aktualnie podglądany motyw jako domyślny. */}
            <div className={styles.themeAcceptBar}>
              <div className={styles.themeAcceptInfo}>
                <span className={styles.themeAcceptLabel}>Podgląd</span>
                <span className={styles.themeAcceptName} title={themeNameOf(themeKey)}>
                  {themeNameOf(themeKey)}
                </span>
                <span className={styles.themeAcceptDefault}>
                  Domyślny: {themeNameOf(defaultThemeKey)}
                </span>
              </div>
              <button
                className={styles.themeAcceptBtn}
                disabled={!previewDiffersFromDefault}
                title={
                  previewDiffersFromDefault
                    ? "Ustaw podglądany motyw jako domyślny"
                    : "Ten motyw jest już domyślny"
                }
                onClick={acceptAsDefault}
              >
                <FontAwesomeIcon icon={faCheck} />
                {previewDiffersFromDefault ? "Ustaw jako domyślny" : "Motyw domyślny"}
              </button>
            </div>
            <input
              ref={themeFileRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={(e) => {
                handleAddCustomTheme(e.target.files?.[0]);
                e.target.value = ""; // pozwól ponownie wybrać ten sam plik
              }}
            />
            <input
              ref={weekdayFileRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={(e) => {
                const idx = weekdayTargetRef.current;
                if (idx != null) handleAddWeekdayTheme(idx, e.target.files?.[0]);
                weekdayTargetRef.current = null;
                e.target.value = "";
              }}
            />
          </div>
        )}
      </div>

      {/* Motyw powitalny — bogaty layout (komunikat + filmy + odtwarzacz Vimeo). */}
      {activeOccasional?.kind === "welcome" && activeOccasional.welcome && (
        <WelcomeTheme welcome={activeOccasional.welcome} onClose={closeOccasional} />
      )}

      {/* Karta motywu okazjonalnego (announcement) — komunikat + CTA.
          Pokazywana zarówno w trybie auto, jak i przy ręcznym podglądzie z menu. */}
      {activeOccasional?.kind !== "welcome" && activeOccasional?.content && (
        <div className={styles.occCard} onClick={(e) => e.stopPropagation()}>
          <button
            className={styles.occClose}
            title="Zamknij"
            onClick={closeOccasional}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
          <div className={styles.occTitle}>{activeOccasional.content.title}</div>
          <div className={styles.occBody}>{activeOccasional.content.body}</div>
          <div className={styles.occActions}>
            {activeOccasional.content.cta && (
              <a
                className={styles.occCta}
                href={activeOccasional.content.cta.url}
                target="_blank"
                rel="noreferrer"
              >
                {activeOccasional.content.cta.label}
              </a>
            )}
            {/* Przyciski cyklu życia tylko w trybie AUTO (nie w ręcznym podglądzie). */}
            {!manualOccasional && occasional?.schedule.dismissible && (
              <button className={styles.occDismiss} onClick={dismissOccasionalPerm}>
                Nie pokazuj ponownie
              </button>
            )}
            {!manualOccasional && occasional?.kind === "announcement" && (
              <button
                className={styles.occReset}
                title="Wyczyść stan demo i pokaż ponownie motyw powitalny, potem webinar"
                onClick={handleResetDemo}
              >
                Reset demo
              </button>
            )}
          </div>
        </div>
      )}

      <aside className={styles.plannerPanel}>
        <header className={styles.plannerHead}>
          <span className={styles.plannerTitle}>Terminarz i zadania</span>
          {/* Bieżący dzień z kalendarza. */}
          <div className={styles.calToday} title="Bieżący dzień">
            <FontAwesomeIcon icon={faCalendarDays} />
            <span className={styles.calTodayText}>
              <span className={styles.calTodayLabel}>Dziś</span>
              <span className={styles.calTodayDate}>{todayLong}</span>
            </span>
          </div>
          <button className={styles.plannerClose} onClick={onClose} title="Zamknij">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </header>

        <div className={styles.plannerTabs}>
          {tabOrder.map((tabId, idx) => (
            <div key={tabId} className={styles.plannerTabWrapper}>
              <button
                className={`${styles.plannerTab} ${tab === tabId ? styles.plannerTabActive : ""}`}
                onClick={() => setTab(tabId)}
              >
                <FontAwesomeIcon icon={TAB_META[tabId].icon} />
                <span>{TAB_META[tabId].label}</span>
              </button>
              <div className={styles.plannerTabReorder}>
                {idx > 0 && (
                  <button
                    className={styles.plannerTabMoveBtn}
                    onClick={(e) => { e.stopPropagation(); moveTab(tabId, -1); }}
                    title="Przesuń w lewo"
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>
                )}
                {idx < tabOrder.length - 1 && (
                  <button
                    className={styles.plannerTabMoveBtn}
                    onClick={(e) => { e.stopPropagation(); moveTab(tabId, 1); }}
                    title="Przesuń w prawo"
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className={styles.plannerBody} ref={calBodyRef}>
          {tab === "cal" && (
            <>
              <div className={styles.calToolbar}>
                {/* Nawigacja po kalendarzu do dowolnej daty. */}
                <div className={styles.calNav}>
                  <button className={styles.calNavBtn} title="Poprzedni dzień" onClick={() => shiftViewDate(-1)}>
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>
                  <input
                    type="date"
                    className={styles.calDateInput}
                    value={viewDate}
                    onChange={(e) => e.target.value && setViewDate(e.target.value)}
                  />
                  <button className={styles.calNavBtn} title="Następny dzień" onClick={() => shiftViewDate(1)}>
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                  {viewDate !== todayISO && (
                    <button className={styles.calTodayBtn} onClick={() => setViewDate(todayISO)}>
                      Dziś
                    </button>
                  )}
                </div>

                <button className={styles.todoAddBtn2} onClick={openEvtForm} title="Dodaj termin">
                  <FontAwesomeIcon icon={faPlus} />
                </button>
              </div>

              {evtForm && (
                <div className={styles.contactCard}>
                  <div className={styles.contactField}>
                    <label>Tytuł wydarzenia</label>
                    <input
                      className={`${styles.contactInput} ${evtError?.field === "title" ? styles.inputError : ""}`}
                      autoFocus
                      placeholder="np. Rozprawa — sygn. I C 220/26"
                      value={evtForm.title}
                      onChange={(e) => {
                        setEvtForm((f) => (f ? { ...f, title: e.target.value } : f));
                        if (evtError?.field === "title") setEvtError(null);
                      }}
                    />
                    {evtError?.field === "title" && (
                      <span className={styles.fieldError}>{evtError.msg}</span>
                    )}
                  </div>

                  {/* Opis/miejsce + Typ wydarzenia + Teczka w jednym wierszu */}
                  <div className={styles.contactRow}>
                    <div className={styles.contactField}>
                      <label>Opis / miejsce</label>
                      <input
                        className={styles.contactInput}
                        placeholder="np. Sąd Rejonowy, sala 14"
                        value={evtForm.sub}
                        onChange={(e) => setEvtForm((f) => (f ? { ...f, sub: e.target.value } : f))}
                      />
                    </div>

                    <div className={styles.contactField}>
                      <label>Typ wydarzenia</label>
                      <input
                        className={styles.contactInput}
                        placeholder="Wybierz lub wpisz nowy typ…"
                        value={evtForm.typeName}
                        onFocus={() => setTypeMenuOpen(true)}
                        onChange={(e) => {
                          setEvtForm((f) => (f ? { ...f, typeName: e.target.value, typeId: null } : f));
                          setTypeMenuOpen(true);
                        }}
                      />
                      {typeMenuOpen && (typeSuggestions.length > 0 || canAddType) && (
                        <div className={styles.contactSuggest}>
                          {typeSuggestions.map((t) => (
                            <button key={t.id} className={`${styles.contactSuggestItem} ${styles.typeItem}`} onClick={() => pickType(t)}>
                              <span className={styles.typeDot} style={{ background: t.accent }} />
                              <span className={styles.contactSuggestName}>{t.name}</span>
                            </button>
                          ))}
                          {canAddType && (
                            <button className={styles.contactSuggestAdd} onClick={() => addType(typeQuery)}>
                              <FontAwesomeIcon icon={faPlus} /> Dodaj typ „{typeQuery}"
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    <div className={styles.contactField}>
                      <label>Teczka</label>
                      <input
                        className={styles.contactInput}
                        placeholder="Szukaj teczki…"
                        value={evtForm.caseName}
                        onFocus={() => setCaseMenuOpen(true)}
                        onChange={(e) => {
                          setEvtForm((f) => (f ? { ...f, caseName: e.target.value, caseId: "" } : f));
                          setCaseMenuOpen(true);
                        }}
                      />
                      {evtForm.caseId && (
                        <button className={styles.caseClear} title="Odepnij teczkę" onClick={clearCase}>
                          <FontAwesomeIcon icon={faXmark} />
                        </button>
                      )}
                      {caseMenuOpen && caseSearch.length >= 2 && (
                        <div className={styles.contactSuggest}>
                          {caseLoading && (
                            <div className={styles.contactSuggestEmpty}>Szukam…</div>
                          )}
                          {!caseLoading && caseSuggestions.length === 0 && (
                            <div className={styles.contactSuggestEmpty}>Brak pasujących teczek</div>
                          )}
                          {caseSuggestions.map((c) => (
                            <button key={c.Id} className={styles.contactSuggestItem} onClick={() => pickCase(c)}>
                              <span className={styles.contactSuggestName}>
                                {[c.Signature, c.Title].filter(Boolean).join(" — ")}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={styles.contactRow}>
                    <div className={styles.contactField}>
                      <label>Czas trwania / slot</label>
                      <select
                        className={styles.contactInput}
                        value={evtForm.dur}
                        onChange={(e) =>
                          setEvtForm((f) => (f ? { ...f, dur: Number(e.target.value) } : f))
                        }
                      >
                        {DUR_OPTIONS.map((m) => (
                          <option key={m} value={m}>{m} min</option>
                        ))}
                      </select>
                    </div>
                    <div className={styles.contactField}>
                      <label>Data</label>
                      <input
                        type="date"
                        className={`${styles.contactInput} ${evtError?.field === "date" ? styles.inputError : ""}`}
                        value={evtForm.date}
                        onChange={(e) => {
                          setEvtForm((f) => (f ? { ...f, date: e.target.value } : f));
                          if (evtError?.field === "date") setEvtError(null);
                        }}
                      />
                      {evtError?.field === "date" && (
                        <span className={styles.fieldError}>{evtError.msg}</span>
                      )}
                    </div>
                    <div className={styles.contactField}>
                      <label>Godzina</label>
                      <input
                        type="time"
                        className={`${styles.contactInput} ${evtError?.field === "time" ? styles.inputError : ""}`}
                        value={evtForm.time}
                        onChange={(e) => {
                          setEvtForm((f) => (f ? { ...f, time: e.target.value } : f));
                          if (evtError?.field === "time") setEvtError(null);
                        }}
                      />
                      {evtError?.field === "time" && (
                        <span className={styles.fieldError}>{evtError.msg}</span>
                      )}
                    </div>
                    <div className={styles.contactField}>
                      <label>&nbsp;</label>
                      <button
                        className={styles.todoMoveBtn}
                        onClick={() => setEvtSlotsOpen((v) => !v)}
                        title="Podpowiedz wolne terminy dla wybranego slotu"
                      >
                        <FontAwesomeIcon icon={faMagnifyingGlass} /> Wolny termin
                      </button>
                    </div>
                  </div>

                  {evtSlotsOpen && (
                    <div className={styles.moveBox}>
                      <div className={styles.moveTitle}>Wolne terminy ({evtForm.dur} min) — wybierz</div>
                      <div className={styles.slotsList}>
                        {freeSlotsForDur(evtForm.dur).map((s, i) => (
                          <button key={i} className={styles.slotBtn} onClick={() => pickEvtSlot(s.date, s.time)}>
                            {dayLabel(s.date)} · {s.time}
                          </button>
                        ))}
                        {freeSlotsForDur(evtForm.dur).length === 0 && (
                          <span className={styles.todoEmpty}>Brak wolnych slotów tej długości</span>
                        )}
                      </div>
                    </div>
                  )}

                  <div className={styles.contactBtns}>
                    {evtError && (
                      <span className={styles.formError}>{evtError.msg}</span>
                    )}
                    <button className={styles.moveCancel} onClick={cancelEvtForm}>Anuluj</button>
                    <button className={styles.moveApply} onClick={saveEvent}>Zapisz termin</button>
                  </div>
                </div>
              )}

              <div
                ref={agendaRef}
                className={styles.calAgenda}
                style={
                  agendaMaxHeight != null
                    ? { maxHeight: agendaMaxHeight, overflow: "hidden" }
                    : undefined
                }
              >
                {agendaCandidates.map((day, di) => {
                  const parts = dayHeaderParts(day.date);
                  const tag =
                    day.date === todayISO ? "Dziś" : day.date === tomorrowISO ? "Jutro" : null;
                  return (
                    <div
                      key={day.date}
                      ref={(el) => (dayBlockRefs.current[di] = el)}
                      className={styles.calGroup}
                    >
                      <div className={styles.calDay}>
                        {tag && <span className={styles.calDayTag}>{tag}</span>}
                        <span className={styles.calDayName}>{parts.weekday}</span>
                        <span className={styles.calDayDate}>{parts.dm}</span>
                        <span className={styles.todoSectionCount}>{day.list.length}</span>
                      </div>
                      {day.list.length === 0 && (
                        <div className={styles.todoEmpty}>Brak wydarzeń tego dnia</div>
                      )}
                      {day.list.map((ev) => renderEvent(ev))}
                    </div>
                  );
                })}
              </div>

              {/* Usunięte terminy (kosz) — zwinięta sekcja z czyszczeniem zawartości */}
              {deletedEvents.length > 0 && (
                <div className={styles.todoGroup}>
                  <div
                    className={styles.todoDay}
                    onClick={() => toggleInSet(setCollapsedSections, "evtDeleted")}
                    style={{ cursor: "pointer" }}
                  >
                    <FontAwesomeIcon
                      className={styles.todoSectionChevron}
                      icon={collapsedSections.has("evtDeleted") ? faChevronRight : faChevronDown}
                    />
                    Usunięte
                    <span className={styles.todoSectionCount}>{deletedEvents.length}</span>
                    <div className={styles.todoHeadActions}>
                      <button
                        className={styles.todoMoveBtn2}
                        title="Wyczyść usunięte (trwale)"
                        onClick={(e) => {
                          e.stopPropagation();
                          clearDeletedEvents();
                        }}
                      >
                        <FontAwesomeIcon icon={faXmark} />
                      </button>
                    </div>
                  </div>
                  {!collapsedSections.has("evtDeleted") &&
                    deletedEvents.map((ev) => (
                      <div
                        key={ev.id}
                        className={`${styles.calItem} ${styles.calItemDeleted}`}
                        style={{ ["--accent" as string]: ev.accent }}
                      >
                        <span className={styles.calTime}>
                          <FontAwesomeIcon icon={faClock} /> {ev.time}
                        </span>
                        <span className={styles.calText}>
                          <span className={styles.calTitle}>{ev.title}</span>
                          <span className={styles.calSub}>
                            {[dayLabel(ev.date), ev.type, ev.sub].filter(Boolean).join(" · ")}
                          </span>
                        </span>
                        <div className={styles.calActions}>
                          <button
                            className={styles.calIconBtn}
                            title="Przywróć termin"
                            onClick={() => restoreEvent(ev.id)}
                          >
                            <FontAwesomeIcon icon={faRotateLeft} />
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </>
          )}
          {tab === "todo" && (
            <>
              {section("yesterday", "Wczoraj", yesterday, false, undefined, "yesterday")}
              {section("today", "Dziś", today, true, "today", "today")}
              {section("tomorrow", "Jutro", tomorrow, true, "tomorrow")}

              {/* Skontaktuj się — kontakty do wykonania + karta z podpowiedziami z bazy Podmioty */}
              <div className={styles.todoGroup}>
                <div
                  className={styles.todoDay}
                  onClick={() => toggleInSet(setCollapsedSections, "contact")}
                  style={{ cursor: "pointer" }}
                >
                  <FontAwesomeIcon
                    className={styles.todoSectionChevron}
                    icon={collapsedSections.has("contact") ? faChevronRight : faChevronDown}
                  />
                  Skontaktuj się
                  <span className={styles.todoSectionCount}>{activeContacts.length}</span>
                  <div className={styles.todoHeadActions}>
                    <button
                      className={styles.todoAddBtn2}
                      title="Dodaj kontakt"
                      onClick={(e) => {
                        e.stopPropagation();
                        openContactForm();
                      }}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                    </button>
                  </div>
                </div>
                {!collapsedSections.has("contact") && (
                  <>
                    {contactForm && (
                      <div className={styles.cForm}>
                        <div className={styles.cGrid}>
                          {/* Kolumna 1: Imię i nazwisko + Cel kontaktu */}
                          <div className={styles.cCol}>
                            <div className={styles.cField}>
                              <label>Imię i nazwisko</label>
                              <input
                                className={styles.cInput}
                                autoFocus
                                placeholder="np. Jan Kowalski"
                                value={contactForm.name}
                                onChange={(e) => setContactForm((f) => (f ? { ...f, name: e.target.value } : f))}
                              />
                              {suggestions.length > 0 && (
                                <div className={styles.contactSuggest}>
                                  {suggestions.map((p) => (
                                    <button
                                      key={p.id}
                                      className={styles.contactSuggestItem}
                                      onClick={() => pickPodmiot(p)}
                                    >
                                      <span className={styles.contactSuggestName}>{p.name}</span>
                                      <span className={styles.contactSuggestMeta}>{p.phone} · {p.email}</span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className={styles.cField}>
                              <label>Cel kontaktu</label>
                              <input
                                className={styles.cInput}
                                placeholder="np. uzgodnienie terminu rozprawy"
                                value={contactForm.purpose}
                                onFocus={() => setPurposeMenuOpen(true)}
                                onChange={(e) => {
                                  setContactForm((f) => (f ? { ...f, purpose: e.target.value } : f));
                                  setPurposeMenuOpen(true);
                                }}
                              />
                              {purposeMenuOpen && purposeSuggestions.length > 0 && (
                                <div className={styles.contactSuggest}>
                                  {purposeSuggestions.map((pp) => (
                                    <button
                                      key={pp}
                                      className={styles.contactSuggestItem}
                                      onClick={() => pickPurpose(pp)}
                                    >
                                      <span className={styles.contactSuggestName}>{pp}</span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Kolumna 2: Data i godzina + Telefon */}
                          <div className={styles.cCol}>
                            <div className={styles.cField}>
                              <label>Data i godzina</label>
                              <div className={styles.cInline}>
                                <input
                                  type="date"
                                  className={styles.cInput}
                                  value={contactForm.date}
                                  onChange={(e) => setContactForm((f) => (f ? { ...f, date: e.target.value } : f))}
                                />
                                <input
                                  type="time"
                                  className={styles.cInput}
                                  value={contactForm.time}
                                  onChange={(e) => setContactForm((f) => (f ? { ...f, time: e.target.value } : f))}
                                />
                              </div>
                            </div>
                            <div className={styles.cField}>
                              <label>Telefon</label>
                              <input
                                className={styles.cInput}
                                placeholder="+48 …"
                                value={contactForm.phone}
                                onChange={(e) => setContactForm((f) => (f ? { ...f, phone: e.target.value } : f))}
                              />
                            </div>
                          </div>
                        </div>

                        <div className={styles.contactBtns}>
                          <button className={styles.moveCancel} onClick={cancelContactForm}>Anuluj</button>
                          <button className={styles.moveApply} onClick={saveContactForm}>Zapisz</button>
                        </div>
                      </div>
                    )}
                    {activeContacts.length === 0 && !contactForm && (
                      <div className={styles.todoEmpty}>Brak zaplanowanych kontaktów</div>
                    )}
                    {activeContacts.map((c) => renderContact(c))}
                  </>
                )}
              </div>

              {/* Wykonane — wykonane zadania ToDo oraz zaznaczone kontakty. */}
              {(() => {
                const total = done.length + doneContacts.length;
                if (total === 0) return null;
                const collapsed = collapsedSections.has("done");
                return (
                  <div className={styles.todoGroup}>
                    <div
                      className={styles.todoDay}
                      onClick={() => toggleInSet(setCollapsedSections, "done")}
                      style={{ cursor: "pointer" }}
                    >
                      <FontAwesomeIcon
                        className={styles.todoSectionChevron}
                        icon={collapsed ? faChevronRight : faChevronDown}
                      />
                      Wykonane
                      <span className={styles.todoSectionCount}>{total}</span>
                    </div>
                    {!collapsed && (
                      <>
                        {done.map((t) => renderTask(t, null))}
                        {doneContacts.map((c) => renderContact(c))}
                      </>
                    )}
                  </div>
                );
              })()}
            </>
          )}
          {tab === "mail" && (
            <div className={styles.mailTab}>
              <div className={styles.mailSection}>
                <div className={styles.mailSectionHead}>
                  <FontAwesomeIcon icon={faEnvelope} />
                  <span>Nowa poczta</span>
                </div>
                <DeskPackages onClick={(pack) => setSelectedPackage(pack)} />
              </div>
              <div className={styles.mailSection}>
                <div className={styles.mailSectionHead}>
                  <FontAwesomeIcon icon={faBell} />
                  <span>Przypominajki</span>
                  <button
                    className={styles.todoAddBtn2}
                    onClick={() => setShowAddReminder(true)}
                    title="Dodaj przypominajkę"
                  >
                    <FontAwesomeIcon icon={faPlus} />
                  </button>
                </div>
                <div className={styles.mailEmpty}>
                  Kliknij +, aby dodać przypominajkę
                </div>
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* Formatka notatki (istniejący komponent AddNote) — z powiązaniem z teczką.
          Notatki z CaseId trafiają automatycznie do zakładki Notatki teczki. */}
      {noteContact && (
        <AddNote
          withCasePicker
          slide
          slideOffsetRight={730}
          toggle={closeNote}
          refetch={() => undefined}
          addNote={(n) => onNoteCreated(noteContact.id, n)}
          defaultValues={
            noteContact.note
              ? { id: noteContact.note.id, title: noteContact.note.title, content: noteContact.note.content }
              : undefined
          }
        />
      )}

      {showAddReminder && (
        <AddNote
          withCasePicker
          slide
          slideOffsetRight={730}
          toggle={() => setShowAddReminder(false)}
          refetch={() => undefined}
          addNote={() => {}}
        />
      )}

      {selectedPackage && ReactDOM.createPortal(
        <div
          className={styles.pkgSlideOverlay}
          onClick={(e) => e.target === e.currentTarget && setSelectedPackage(null)}
        >
          <div className={styles.pkgSlidePanel}>
            <div className={styles.pkgSlideHead}>
              <span>Podgląd przesyłki</span>
              <button className={styles.pkgSlideClose} onClick={() => setSelectedPackage(null)}>
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className={styles.pkgSlideContent}>
              <Package
                kind={selectedPackage.Kind.toString()}
                packageId={selectedPackage.Id}
                onDownload={() => {}}
                onPreview={() => {}}
                showVersion={false}
              />
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default DeskPlanner;
